import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import wabaApi from '../../api/services/wabaApi';
import whatsappConfigApi from '../../api/services/whatsappConfigApi';
import toast from 'react-hot-toast';
import { useData } from '../../contexts/DataContext';
import {
  Settings as SettingsIcon, 
  Save, 
  X, 
  Edit, 
  MessageSquare, 
  Database,
  CheckCircle,
  CreditCard,
  Plus,
  Palette,
  Globe,
  Facebook,
  Twitter,
  Linkedin,
  Zap,
  Calendar as CalendarIcon
} from 'lucide-react';
import orgApi from '../../api/services/organizationApi';
import academicYearApi from '../../api/services/academicYearApi';
import ConfirmationModal from '../../ui/ConfirmationModal';
import Subscription from '../Subscription/Subscription';

const Settings = () => {
  const { isAdmin } = useAuth();
  
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <SettingsContent />;
};

const SettingsContent = () => {
  const { user, refreshUser } = useAuth();
  const { loadOrganization, loadCurrentAcademicYear } = useData();

  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  /* WhatsApp configuration state */
  const [whatsappSettings, setWhatsappSettings] = useState({
    businessAccountId: '',
    accessToken: '',
    verifyToken: '',
    webhookUrl: '',
  });

  const [generalSettings, setGeneralSettings] = useState({
    schoolName: '',
    schoolAddress: '',
    schoolPhone: '',
    schoolEmail: '',
    timezone: 'Asia/Kolkata',
    language: 'en',
    orgCode: '',
    orgType: ''
  });

  const [paymentConfig, setPaymentConfig] = useState({
    razorpay: {
      keyId: '',
      keySecret: '',
      webhookSecret: '',
      isActive: false
    }
  });

  const tabs = [
    { id: 'general', label: 'General', icon: SettingsIcon },
    { id: 'academic', label: 'Academic Years', icon: CalendarIcon },
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
    { id: 'payment', label: 'Payment', icon: CreditCard },
    { id: 'subscription', label: 'Subscription', icon: Zap }
  ];

  const [academicYears, setAcademicYears] = useState([]);
  const [showAddYearModal, setShowAddYearModal] = useState(false);
  const [newYearData, setNewYearData] = useState({
    year: '',
    startDate: '',
    endDate: ''
  });

  const [brandingSettings, setBrandingSettings] = useState({
    logoUrl: '',
    websiteUrl: '',
    socialLinks: {
      facebook: '',
      twitter: '',
      linkedin: ''
    },
    brandColors: {
      primary: '#4f46e5',
      secondary: '#7c3aed'
    },
    supportContact: {
        phone: '',
        email: '',
        address: ''
    }
  });

  /* Removed unused configuration arrays (timezones, languages etc can remain or be simplified as needed, but keeping for General tab) */
  const timezones = [
    { value: 'Asia/Kolkata', label: 'India (UTC+5:30)' },
    { value: 'Asia/Dubai', label: 'UAE (UTC+4)' },
    { value: 'Asia/Singapore', label: 'Singapore (UTC+8)' },
    { value: 'America/New_York', label: 'Eastern Time (UTC-5)' },
    { value: 'Europe/London', label: 'London (UTC+0)' }
  ];

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'hi', label: 'Hindi' },
    { value: 'gu', label: 'Gujarati' },
    { value: 'bn', label: 'Bengali' },
    { value: 'ta', label: 'Tamil' }
  ];

  const loadConfiguration = useCallback(async () => {
    try {
      if (activeTab === 'whatsapp') {
          const response = await whatsappConfigApi.getConfiguration();
          if (response.success && response.data) {
            setWhatsappSettings(prev => ({
              ...prev,
              businessAccountId: response.data.phoneNumberId || '',
              verifyToken: response.data.verifyToken || '',
              webhookUrl: response.data.webhookUrl || '',
            }));
          }
      } else if (activeTab === 'academic') {
          const response = await academicYearApi.getList();
          if (response.success) {
            setAcademicYears(Array.isArray(response.data) ? response.data : []);
          }
      } else if (activeTab === 'general' || activeTab === 'branding' || activeTab === 'payment') {
          const response = await orgApi.getOrganizationDetails();
          if (response?.success && response?.data) {
              const org = response.data;
              if (activeTab === 'payment' && org.paymentConfig) {
                 setPaymentConfig(org.paymentConfig);
              }
              // Map organization details to state
              setGeneralSettings({
                  schoolName: org.organizationName || '',
                  schoolAddress: org.fullAddress || '',
                  schoolPhone: org.contactInfo?.adminContact || org.contactInfo?.supportHotline || '',
                  schoolEmail: org.contactInfo?.officialEmail || '',
                  timezone: org.timezone || 'Asia/Kolkata',
                  language: org.language || 'en',
                  orgCode: org.orgCode || '',
                  orgType: org.orgType || ''
              });
              
              setBrandingSettings({
                  logoUrl: org.logoUrl || org.branding?.logoUrl || '',
                  websiteUrl: org.websiteUrl || '',
                  socialLinks: {
                      facebook: org.contactInfo?.facebook || '',
                      twitter: org.contactInfo?.twitter || '',
                      linkedin: org.contactInfo?.linkedin || ''
                  },
                  brandColors: org.brandColors || org.branding?.brandColors || { primary: '#4f46e5', secondary: '#7c3aed' },
                  supportContact: {
                      phone: org.contactInfo?.adminContact || org.contactInfo?.supportHotline || '',
                      email: org.contactInfo?.officialEmail || '',
                      address: org.fullAddress || ''
                  }
              });
          }
      }
    } catch (error) {
    }
  }, [activeTab]);

  // Load existing configuration on mount and when tab changes
  useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration]);

  const handleSave = async () => {
    if (activeTab === 'general' || activeTab === 'branding') {
        await handleSaveOrganizationProfile();
    } else {
        setLoading(true);
        try {
          // generic fallback if needed, but tabs have their own handlers mostly
          await new Promise(resolve => setTimeout(resolve, 1000));
          setIsEditing(false);
          toast.success('Settings saved successfully!');
        } catch (error) {
          toast.error('Failed to save settings');
        } finally {
          setLoading(false);
        }
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset to original values
  };

  // Validate WhatsApp credentials
  const handleValidateCredentials = async () => {
    if (!whatsappSettings.businessAccountId || !whatsappSettings.accessToken) {
      toast.error('Phone Number ID and Access Token are required');
      return false;
    }

    setValidating(true);
    setValidationSuccess(false);
    try {
      const response = await whatsappConfigApi.validateCredentials(
        whatsappSettings.businessAccountId,
        whatsappSettings.accessToken
      );
      
      if (response.success && response.data.isValid) {
        setValidationSuccess(true);
        toast.success('✓ Credentials validated successfully!');
        return true;
      } else {
        toast.error('Invalid credentials. Please check your Phone Number ID and Access Token.');
        return false;
      }
    } catch (error) {
      toast.error(error.message || 'Validation failed. Please try again.');
      return false;
    } finally {
      setValidating(false);
    }
  };

  const handleSavePaymentConfig = async () => {
    setLoading(true);
    try {
        if (!user?.orgId) {
            toast.error("Organization ID not found");
            return;
        }

        const payload = {
            paymentConfig: paymentConfig
        };

        const response = await orgApi.updateOrganization(user.orgId, payload);
        
        if (response?.success) {
            toast.success('Payment configuration saved successfully!');
            setIsEditing(false);
        } else {
            toast.error(response?.message || 'Failed to update payment settings');
        }
    } catch (error) {
      toast.error(error.message || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

 // Save WhatsApp configuration
  const handleSaveWhatsAppConfig = async () => {
    // Validate first
    const isValid = await handleValidateCredentials();
    if (!isValid) return;

    setLoading(true);
    try {
      const configData = {
        phoneNumberId: whatsappSettings.businessAccountId,
        whatsappToken: whatsappSettings.accessToken,
        verifyToken: whatsappSettings.verifyToken || undefined,
        webhookUrl: whatsappSettings.webhookUrl || undefined,
      };

      const response = await whatsappConfigApi.saveConfiguration(configData);
      
      if (response.success) {
        toast.success('WhatsApp configuration saved successfully!');
        // Clear access token from form for security
        setWhatsappSettings(prev => ({ ...prev, accessToken: '' }));
        setValidationSuccess(false);
        
        // Optionally initialize templates
        if (user?.orgId) {
          try {
            await wabaApi.initWaba({
              orgId: user.orgId,
              wabaId: whatsappSettings.businessAccountId,
              userId: user?._id || user?.id
            });
            toast.success('Templates initialized successfully!');
          } catch (templateError) {
            // Don't show error as config was saved successfully
          }
        }
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  // Delete WhatsApp configuration
  const handleDeleteWhatsAppConfig = () => {
    setShowDeleteModal(true);
  };

  const confirmDeleteWhatsAppConfig = async () => {
    setIsDeleting(true);
    setLoading(true); // Keep general loading too
    try {
      const response = await whatsappConfigApi.deleteConfiguration();
      
      if (response.success) {
        toast.success('WhatsApp configuration deactivated successfully');
        setWhatsappSettings({
          businessAccountId: '',
          accessToken: '',
          verifyToken: '',
          webhookUrl: '',
        });
        setValidationSuccess(false);
        setShowDeleteModal(false);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to deactivate configuration');
    } finally {
      setIsDeleting(false);
      setLoading(false);
    }
  };

  const handleSetCurrentYear = async (id) => {
    setLoading(true);
    try {
      const response = await academicYearApi.setCurrent(id);
      if (response.success) {
        toast.success('Academic year set as current!');
        await loadConfiguration();
        // Also refresh user to get updated organization info
        if (typeof refreshUser === 'function') {
           await refreshUser();
        }
        if (typeof loadOrganization === 'function') {
           await loadOrganization();
        }
        if (typeof loadCurrentAcademicYear === 'function') {
           await loadCurrentAcademicYear();
        }
      } else {
        toast.error(response.message || 'Failed to set current year');
      }
    } catch (error) {
      toast.error('Failed to set current year');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateYear = async () => {
    if (!newYearData.year || !newYearData.startDate || !newYearData.endDate) {
      toast.error('Please fill all fields');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        academicYear: newYearData.year,
        startDate: newYearData.startDate,
        endDate: newYearData.endDate
      };
      const response = await academicYearApi.create(payload);
      if (response.success) {
        toast.success('Academic year created!');
        setShowAddYearModal(false);
        setNewYearData({ year: '', startDate: '', endDate: '' });
        loadConfiguration();
      }
    } catch (error) {
      toast.error('Failed to create academic year');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOrganizationProfile = async () => {
    setLoading(true);
    try {
      const payload = {
        organizationName: generalSettings.schoolName,
        fullAddress: generalSettings.schoolAddress,
        timezone: generalSettings.timezone,
        language: generalSettings.language,
        logoUrl: brandingSettings.logoUrl,
        websiteUrl: brandingSettings.websiteUrl,
        brandColors: brandingSettings.brandColors,
        contactInfo: {
          officialEmail: generalSettings.schoolEmail,
          adminContact: generalSettings.schoolPhone,
          facebook: brandingSettings.socialLinks.facebook,
          twitter: brandingSettings.socialLinks.twitter,
          linkedin: brandingSettings.socialLinks.linkedin
        }
      };

      const response = await orgApi.updateOrganizationProfile(payload);
      if (response.success) {
        toast.success('Organization settings saved!');
        setIsEditing(false);
        // Apply theme if colors changed
        document.documentElement.style.setProperty('--primary-color', brandingSettings.brandColors.primary);
        document.documentElement.style.setProperty('--secondary-color', brandingSettings.brandColors.secondary);
      } else {
        toast.error(response.message || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save organization settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBranding = handleSaveOrganizationProfile;

  const renderAcademicSettings = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-blue-50 p-4 rounded-xl border border-blue-100">
        <div>
          <h4 className="font-semibold text-blue-900">Academic Year Management</h4>
          <p className="text-sm text-blue-700">Set up and manage academic sessions for your institution.</p>
        </div>
        <button 
          onClick={() => setShowAddYearModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          <span>Add Year</span>
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Array.isArray(academicYears) && academicYears.map((ay) => (
              <tr key={ay._id || ay.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{ay.academicYear || ay.year}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {ay.startDate ? new Date(ay.startDate).toLocaleDateString() : 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {ay.endDate ? new Date(ay.endDate).toLocaleDateString() : 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {ay.isCurrent ? (
                    <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 border border-green-200 shadow-sm">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Current
                    </span>
                  ) : (
                    <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                      Inactive
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {!ay.isCurrent && (
                    <button 
                      onClick={() => handleSetCurrentYear(ay._id || ay.id)}
                      disabled={loading}
                      className="text-primary-600 hover:text-primary-900 bg-primary-50 px-3 py-1 rounded-lg transition-colors border border-primary-100"
                    >
                      Set as Current
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {(!Array.isArray(academicYears) || academicYears.length === 0) && (
              <tr>
                <td colSpan="5" className="px-6 py-10 text-center text-gray-500 italic">
                  No academic years found. Click "Add Year" to create one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showAddYearModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl">
            <h3 className="text-xl font-bold mb-6">Add New Academic Year</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year Name</label>
                <input 
                  type="text" 
                  placeholder="e.g. 2025-26" 
                  className="input-field"
                  value={newYearData.year}
                  onChange={e => setNewYearData({...newYearData, year: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input 
                  type="date" 
                  className="input-field" 
                  value={newYearData.startDate}
                  onChange={e => setNewYearData({...newYearData, startDate: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input 
                  type="date" 
                  className="input-field"
                  value={newYearData.endDate}
                  onChange={e => setNewYearData({...newYearData, endDate: e.target.value})}
                />
              </div>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setShowAddYearModal(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleCreateYear} className="btn-primary">Create Year</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderBrandingSettings = () => (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Visual Identity */}
        <div className="space-y-6">
          <h4 className="font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
            <Palette className="h-4 w-4 text-primary-600" />
            Visual Identity
          </h4>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Institutional Logo URL</label>
            <div className="flex items-center gap-4">
              <input 
                type="text" 
                value={brandingSettings.logoUrl} 
                onChange={e => setBrandingSettings({...brandingSettings, logoUrl: e.target.value})}
                disabled={!isEditing}
                className="input-field flex-1"
                placeholder="https://..."
              />
              {brandingSettings.logoUrl && (
                <img src={brandingSettings.logoUrl} alt="Logo Preview" className="h-10 w-10 object-contain rounded border" />
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
              <div className="flex items-center gap-2">
                <input 
                  type="color" 
                  value={brandingSettings.brandColors.primary} 
                  onChange={e => setBrandingSettings({...brandingSettings, brandColors: {...brandingSettings.brandColors, primary: e.target.value}})}
                  disabled={!isEditing}
                  className="h-10 w-10 p-0 border-0 rounded-lg cursor-pointer"
                />
                <input 
                    type="text" 
                    value={brandingSettings.brandColors.primary} 
                    onChange={e => setBrandingSettings({...brandingSettings, brandColors: {...brandingSettings.brandColors, primary: e.target.value}})}
                    disabled={!isEditing}
                    className="input-field flex-1 text-xs"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Color</label>
              <div className="flex items-center gap-2">
                <input 
                  type="color" 
                  value={brandingSettings.brandColors.secondary} 
                  onChange={e => setBrandingSettings({...brandingSettings, brandColors: {...brandingSettings.brandColors, secondary: e.target.value}})}
                  disabled={!isEditing}
                  className="h-10 w-10 p-0 border-0 rounded-lg cursor-pointer"
                />
                <input 
                    type="text" 
                    value={brandingSettings.brandColors.secondary} 
                    onChange={e => setBrandingSettings({...brandingSettings, brandColors: {...brandingSettings.brandColors, secondary: e.target.value}})}
                    disabled={!isEditing}
                    className="input-field flex-1 text-xs"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Social & Contact */}
        <div className="space-y-6">
          <h4 className="font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
            <Globe className="h-4 w-4 text-primary-600" />
            Social & Online Presence
          </h4>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Official Website</label>
            <input 
              type="text" 
              value={brandingSettings.websiteUrl}
              onChange={e => setBrandingSettings({...brandingSettings, websiteUrl: e.target.value})}
              disabled={!isEditing}
              className="input-field"
              placeholder="https://www.school.edu"
            />
          </div>

          <div className="space-y-3">
             <div className="relative">
                <Facebook className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input 
                    type="text" 
                    placeholder="Facebook Profile URL"
                    value={brandingSettings.socialLinks.facebook}
                    onChange={e => setBrandingSettings({...brandingSettings, socialLinks: {...brandingSettings.socialLinks, facebook: e.target.value}})}
                    disabled={!isEditing}
                    className="input-field pl-10"
                />
             </div>
             <div className="relative">
                <Twitter className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input 
                    type="text" 
                    placeholder="Twitter Profile URL"
                    value={brandingSettings.socialLinks.twitter}
                    onChange={e => setBrandingSettings({...brandingSettings, socialLinks: {...brandingSettings.socialLinks, twitter: e.target.value}})}
                    disabled={!isEditing}
                    className="input-field pl-10"
                />
             </div>
             <div className="relative">
                <Linkedin className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input 
                    type="text" 
                    placeholder="LinkedIn Profile URL"
                    value={brandingSettings.socialLinks.linkedin}
                    onChange={e => setBrandingSettings({...brandingSettings, socialLinks: {...brandingSettings.socialLinks, linkedin: e.target.value}})}
                    disabled={!isEditing}
                    className="input-field pl-10"
                />
             </div>
          </div>
        </div>
      </div>

      {isEditing && (
        <div className="flex justify-end pt-4 border-t">
          <button 
            onClick={handleSaveBranding}
            disabled={loading}
            className="btn-primary flex items-center gap-2"
          >
            {loading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Save className="h-4 w-4" />}
            <span>Save Branding Settings</span>
          </button>
        </div>
      )}
    </div>
  );

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-6 mb-2">
            <div>
                <label className="block text-sm font-bold text-gray-500 mb-1 uppercase tracking-tight">Org Code</label>
                <div className="bg-gray-100 px-4 py-2 rounded-lg text-sm font-bold text-slate-700 border border-gray-200">
                    {generalSettings.orgCode || 'N/A'}
                </div>
            </div>
            <div>
                <label className="block text-sm font-bold text-gray-500 mb-1 uppercase tracking-tight">Org Type</label>
                <div className="bg-gray-100 px-4 py-2 rounded-lg text-sm font-bold text-slate-700 border border-gray-200">
                    {generalSettings.orgType || 'N/A'}
                </div>
            </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            School Name
          </label>
          <input
            type="text"
            value={generalSettings.schoolName}
            onChange={(e) => setGeneralSettings(prev => ({ ...prev, schoolName: e.target.value }))}
            disabled={!isEditing}
            className={`input-field ${!isEditing ? 'bg-gray-50 cursor-not-allowed' : ''}`}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            School Phone
          </label>
          <input
            type="tel"
            value={generalSettings.schoolPhone}
            onChange={(e) => setGeneralSettings(prev => ({ ...prev, schoolPhone: e.target.value }))}
            disabled={!isEditing}
            className={`input-field ${!isEditing ? 'bg-gray-50 cursor-not-allowed' : ''}`}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            School Email
          </label>
          <input
            type="email"
            value={generalSettings.schoolEmail}
            onChange={(e) => setGeneralSettings(prev => ({ ...prev, schoolEmail: e.target.value }))}
            disabled={!isEditing}
            className={`input-field ${!isEditing ? 'bg-gray-50 cursor-not-allowed' : ''}`}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Timezone
          </label>
          <select
            value={generalSettings.timezone}
            onChange={(e) => setGeneralSettings(prev => ({ ...prev, timezone: e.target.value }))}
            disabled={!isEditing}
            className={`input-field ${!isEditing ? 'bg-gray-50 cursor-not-allowed' : ''}`}
          >
            {timezones.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Language
          </label>
          <select
            value={generalSettings.language}
            onChange={(e) => setGeneralSettings(prev => ({ ...prev, language: e.target.value }))}
            disabled={!isEditing}
            className={`input-field ${!isEditing ? 'bg-gray-50 cursor-not-allowed' : ''}`}
          >
            {languages.map(lang => (
              <option key={lang.value} value={lang.value}>{lang.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          School Address
        </label>
        <textarea
          rows={3}
          value={generalSettings.schoolAddress}
          onChange={(e) => setGeneralSettings(prev => ({ ...prev, schoolAddress: e.target.value }))}
          disabled={!isEditing}
          className={`input-field resize-none ${!isEditing ? 'bg-gray-50 cursor-not-allowed' : ''}`}
        />
      </div>
    </div>
  );

  const renderWhatsAppSettings = () => (
    <div className="space-y-6">
      {/* Configuration Form */}
      <div className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="font-semibold text-gray-900 text-lg">WhatsApp Business API Configuration</h4>
            <p className="text-sm text-gray-600 mt-1">
              Connect your WhatsApp Business Account to enable automated messaging and notifications.
            </p>
          </div>
          <MessageSquare className="h-8 w-8 text-green-600" />
        </div>
        
        <div className="space-y-5 mt-6">
          {/* Phone Number ID - Required */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number ID <span className="text-red-500">*</span>
              <a 
                href="#whatsapp-help" 
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('whatsapp-help')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="ml-2 text-xs text-primary-600 hover:text-primary-700 underline"
              >
                How to get?
              </a>
            </label>
            <input
              type="text"
              value={whatsappSettings.businessAccountId}
              onChange={(e) => {
                setWhatsappSettings(prev => ({ ...prev, businessAccountId: e.target.value.trim() }));
                setValidationSuccess(false); // Reset validation when fields change
              }}
              placeholder="123456789012345"
              className="input-field w-full"
            />
            <p className="mt-1 text-xs text-gray-500">
              15-digit numeric ID from Meta Business Manager
            </p>
          </div>

          {/* WhatsApp Access Token - Required */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              WhatsApp Access Token <span className="text-red-500">*</span>
              <a 
                href="#whatsapp-help"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('whatsapp-help')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="ml-2 text-xs text-primary-600 hover:text-primary-700 underline"
              >
                How to get?
              </a>
            </label>
            <div className="relative">
              <input
                type="password"
                value={whatsappSettings.accessToken || ''}
                onChange={(e) => {
                  setWhatsappSettings(prev => ({ ...prev, accessToken: e.target.value.trim() }));
                  setValidationSuccess(false); // Reset validation when fields change
                }}
                placeholder="EAAxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="input-field w-full font-mono text-sm pr-10"
              />
              {validationSuccess && (
                <CheckCircle className="absolute right-3 top-3 h-5 w-5 text-green-600" />
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Permanent access token from Meta Business (stored securely, never displayed after save)
            </p>
          </div>

          {/* Verify Token - Optional */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verify Token <span className="text-gray-400 text-xs">(Optional)</span>
            </label>
            <input
              type="text"
              value={whatsappSettings.verifyToken || ''}
              onChange={(e) => setWhatsappSettings(prev => ({ ...prev, verifyToken: e.target.value.trim() }))}
              placeholder="my_secure_verify_token_123"
              className="input-field w-full"
            />
            <p className="mt-1 text-xs text-gray-500">
              Custom token for webhook verification (create your own secure string)
            </p>
          </div>

          {/* Webhook URL - Optional */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Webhook URL <span className="text-gray-400 text-xs">(Optional)</span>
            </label>
            <input
              type="url"
              value={whatsappSettings.webhookUrl || ''}
              onChange={(e) => setWhatsappSettings(prev => ({ ...prev, webhookUrl: e.target.value.trim() }))}
              placeholder="https://yourdomain.com/api/whatsapp/webhook"
              className="input-field w-full"
            />
            <p className="mt-1 text-xs text-gray-500">
              Your server's webhook endpoint URL (must be publicly accessible HTTPS)
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleValidateCredentials}
              disabled={!whatsappSettings.businessAccountId || !whatsappSettings.accessToken || validating || loading}
              className="btn-secondary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-md"
            >
              {validating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                  <span>Validating...</span>
                </>
              ) : validationSuccess ? (
                <>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>Validated ✓</span>
                </>
              ) : (
                <>
                  <Database className="h-4 w-4" />
                  <span>Test Connection</span>
                </>
              )}
            </button>

            <button
              onClick={handleSaveWhatsAppConfig}
              disabled={!whatsappSettings.businessAccountId || !whatsappSettings.accessToken || validating || loading}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-lg"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Save Configuration</span>
                </>
              )}
            </button>

            <button
              onClick={handleDeleteWhatsAppConfig}
              disabled={validating || loading}
              className="btn-danger flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-md ml-auto"
            >
              <X className="h-4 w-4" />
              <span>Deactivate</span>
            </button>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div id="whatsapp-help" className="p-6 bg-blue-50 rounded-xl border border-blue-200">
        <h4 className="font-semibold text-blue-900 text-lg mb-4 flex items-center">
          <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">?</span>
          How to Get WhatsApp Business Credentials
        </h4>
        
        <div className="space-y-4 text-sm text-blue-900">
          <div className="bg-white p-4 rounded-lg border border-blue-100">
            <h5 className="font-semibold mb-2">Step 1: Create Meta Business Account</h5>
            <ol className="list-decimal list-inside space-y-1 text-blue-800 ml-2">
              <li>Go to <a href="https://developers.facebook.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-700">Meta for Developers</a></li>
              <li>Create a developer account if you don't have one</li>
              <li>Navigate to <strong>My Apps → Create App</strong></li>
              <li>Select <strong>Business</strong> as app type</li>
            </ol>
          </div>

          <div className="bg-white p-4 rounded-lg border border-blue-100">
            <h5 className="font-semibold mb-2">Step 2: Set Up WhatsApp Business API</h5>
            <ol className="list-decimal list-inside space-y-1 text-blue-800 ml-2">
              <li>In your app dashboard, add <strong>WhatsApp</strong> product</li>
              <li>Navigate to <strong>WhatsApp → Getting Started</strong></li>
              <li>You'll see your temporary credentials here</li>
            </ol>
          </div>

          <div className="bg-white p-4 rounded-lg border border-blue-100">
            <h5 className="font-semibold mb-2">Step 3: Get Phone Number ID</h5>
            <ol className="list-decimal list-inside space-y-1 text-blue-800 ml-2">
              <li>Go to <strong>WhatsApp → API Setup</strong></li>
              <li>Under <strong>From</strong>, you'll see your test phone number</li>
              <li>Click on the phone number to see the <strong>Phone Number ID</strong></li>
              <li>Copy this ID (it's a 15-digit numeric string)</li>
            </ol>
          </div>

          <div className="bg-white p-4 rounded-lg border border-blue-100">
            <h5 className="font-semibold mb-2">Step 4: Generate Permanent Access Token</h5>
            <ol className="list-decimal list-inside space-y-1 text-blue-800 ml-2">
              <li>Go to <strong>WhatsApp → Getting Started</strong> (temporary token expires in 24 hours)</li>
              <li>To create a permanent token, go to <strong>Business Settings → System Users</strong></li>
              <li>Create a system user</li>
              <li>Add WhatsApp permissions to the user</li>
              <li>Generate token and select these permissions:
                <ul className="list-disc list-inside ml-4 mt-1">
                  <li><code className="bg-blue-100 px-1 rounded">whatsapp_business_messaging</code></li>
                  <li><code className="bg-blue-100 px-1 rounded">whatsapp_business_management</code></li>
                </ul>
              </li>
              <li>Copy and <strong>save this token securely</strong> (you won't see it again)</li>
            </ol>
          </div>

          <div className="bg-white p-4 rounded-lg border border-blue-100">
            <h5 className="font-semibold mb-2">Step 5: Set Up Webhook (Optional)</h5>
            <ol className="list-decimal list-inside space-y-1 text-blue-800 ml-2">
              <li>Go to <strong>WhatsApp → Configuration</strong></li>
              <li>Click <strong>Edit</strong> under Webhook</li>
              <li>Enter your webhook URL (must be HTTPS and publicly accessible)</li>
              <li>Enter your verify token (create this yourself - any secure string)</li>
              <li>Subscribe to webhook fields as needed</li>
            </ol>
          </div>

          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-xs text-yellow-800">
              <strong>⚠️ Security Note:</strong> Never share your access token publicly. It will be stored securely on our server and is never displayed after saving.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Temporarily removed admin check for testing
  
  const renderPaymentSettings = () => (
      <div className="space-y-6">
          <div className="p-6 bg-white border border-gray-200 rounded-xl shadow-sm">
              <div className="flex items-start justify-between mb-6">
                  <div>
                      <h4 className="font-semibold text-gray-900 text-lg">Razorpay Payment Gateway</h4>
                      <p className="text-sm text-gray-600 mt-1">
                          Configure Razorpay to accept online fee payments from students.
                      </p>
                  </div>
                  <div className="p-2 bg-indigo-50 rounded-lg">
                      <CreditCard className="h-6 w-6 text-indigo-600" />
                  </div>
              </div>

              <div className="space-y-5">
                  {/* Active Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div>
                          <label className="text-sm font-medium text-gray-900">Enable Razorpay Payments</label>
                          <p className="text-xs text-gray-500 mt-0.5">Allow students to pay fees online using Razorpay</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                          <input 
                              type="checkbox" 
                              checked={paymentConfig.razorpay.isActive}
                              onChange={e => setPaymentConfig(prev => ({
                                  ...prev,
                                  razorpay: { ...prev.razorpay, isActive: e.target.checked }
                              }))}
                              disabled={!isEditing}
                              className="sr-only peer" 
                          />
                          <div className={`w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-indigo-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600 ${!isEditing ? 'opacity-60 cursor-not-allowed' : ''}`}></div>
                      </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="col-span-1">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                              Key ID <span className="text-red-500">*</span>
                          </label>
                          <input
                              type="text" 
                              value={paymentConfig.razorpay.keyId}
                              onChange={e => setPaymentConfig(prev => ({
                                  ...prev,
                                  razorpay: { ...prev.razorpay, keyId: e.target.value }
                              }))}
                              disabled={!isEditing}
                              className={`input-field w-full ${!isEditing ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                              placeholder="rzp_live_..."
                          />
                      </div>
                      <div className="col-span-1">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                              Key Secret <span className="text-red-500">*</span>
                          </label>
                          <input
                              type="password"
                              value={paymentConfig.razorpay.keySecret}
                              onChange={e => setPaymentConfig(prev => ({
                                  ...prev,
                                  razorpay: { ...prev.razorpay, keySecret: e.target.value }
                              }))}
                              disabled={!isEditing}
                              className={`input-field w-full ${!isEditing ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                              placeholder="••••••••••••••••"
                          />
                      </div>
                      <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                              Webhook Secret <span className="text-xs text-gray-400">(Optional but recommended)</span>
                          </label>
                          <input
                              type="password"
                              value={paymentConfig.razorpay.webhookSecret}
                              onChange={e => setPaymentConfig(prev => ({
                                  ...prev,
                                  razorpay: { ...prev.razorpay, webhookSecret: e.target.value }
                              }))}
                              disabled={!isEditing}
                              className={`input-field w-full ${!isEditing ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                              placeholder="secret_..."
                          />
                          <p className="mt-1 text-xs text-gray-500">
                              Used to verify webhook events from Razorpay.
                          </p>
                      </div>
                  </div>
              </div>
          </div>
          
           {isEditing && (
              <div className="flex justify-end">
                   <button
                        onClick={handleSavePaymentConfig}
                        disabled={loading}
                        className="btn-primary flex items-center space-x-2"
                   >
                       {loading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Save className="h-4 w-4" />}
                       <span>Save Payment Settings</span>
                   </button>
              </div>
           )}
      </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Settings</h1>
          <p className="text-gray-600">Configure your educational platform settings</p>
        </div>
        <div className="flex items-center space-x-3">
          {activeTab !== 'subscription' && (
            isEditing ? (
            <>
              <button
                onClick={handleCancel}
                className="btn-secondary flex items-center space-x-2"
              >
                <X className="h-4 w-4" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <Edit className="h-4 w-4" />
              <span>Edit Settings</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </div>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="card">
        {activeTab === 'general' && renderGeneralSettings()}
        {activeTab === 'academic' && renderAcademicSettings()}
        {activeTab === 'branding' && renderBrandingSettings()}
        {activeTab === 'whatsapp' && renderWhatsAppSettings()}
        {activeTab === 'payment' && renderPaymentSettings()}
        {activeTab === 'subscription' && <Subscription isEmbedded={true} />}
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteWhatsAppConfig}
        title="Deactivate WhatsApp Integration"
        message="Are you sure you want to deactivate WhatsApp integration? This will remove all saved credentials and stop all automated messages."
        confirmText="Deactivate"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Settings;
